import React, { useEffect } from 'react';
import Img from 'react-image';
import { connect } from 'react-redux';
import ReactSound from 'react-sound';
import styled from 'styled-components';
import UIFx from 'uifx';
import { useImagesContext } from '../../../contexts/ImagesContext';
import {
  getstudentInfo as getStudentInfoService,
  playHatch,
} from '../../../services/game-play';
import { ImgClick } from '../../utility';
import { useShortcut } from '../../../utils/useShortcut';
import { MainNumber } from './MainNumber';

import {
  gotoCollectionPage,
  playStage,
  setBirdNumber,
  updatePlayData,
} from '../../../actions/hatch-egg';
import { Delay } from '../../../utils/delay';
import soundBackground from '../sounds/main-background.mp3';
import soundGotoPlay from '../sounds/main-goto-play.ogg';
import soundHatchingFailed from '../sounds/main-hatching-failed.mp3';
import soundHatchingSuccess from '../sounds/main-hatching-success.mp3';
import soundUnlocking from '../sounds/main-unlocking.mp3';
import MainStage, { STAGE_STAGE } from './MainStage';
import AnimationWithSound from '../AnimationWithSound';
import soundBirdRest from '../sounds/main-bird-rest.mp3';

const HATCHING_STAGE = {
  idle: 0,
  waiting: 1,
  failed: 2,
  success: 3,
};

function getCurentStage(breakCount) {
  if (breakCount >= 0 && breakCount < 100) {
    return 1;
  } else if (breakCount >= 100 && breakCount < 200) {
    return 2;
  } else if (breakCount >= 200 && breakCount < 300) {
    return 3;
  } else if (breakCount >= 300 && breakCount < 500) {
    return 4;
  } else if (breakCount >= 500) {
    return 5;
  }
  return 0;
}

const MainPage = ({
  history,
  student,
  config,
  gotoCollectionPage,
  playStage,
  setBirdNumber,
  updatePlayData,
  hatchEgg,
}) => {
  const { images } = useImagesContext();
  const [gotoPlay, setGotoPlay] = React.useState(false);
  const [hatchingStage, setHatchingStage] = React.useState(HATCHING_STAGE.idle);
  const [stages, setStages] = React.useState([
    { number: 1, state: STAGE_STAGE.notDisplayed },
    { number: 2, state: STAGE_STAGE.notDisplayed },
    { number: 3, state: STAGE_STAGE.notDisplayed },
    { number: 4, state: STAGE_STAGE.notDisplayed },
    { number: 5, state: STAGE_STAGE.notDisplayed },
  ]);
  const [hatchCountUpdated, setHatchCountUpdated] = React.useState(false);
  const [breakCountUpdated, setBreakCountUpdated] = React.useState(false);
  const [stageNumber, setStageNumber] = React.useState(null);
  const [breakCountValue, setBreakCountValue] = React.useState(0);
  const [hatchCountValue, setHatchCountValue] = React.useState(0);
  const [isBirdRest, setIsBirdRest] = React.useState(false);

  useShortcut(
    '2',
    () => {
      handleShortcut2();
    },
    { disableTextInputs: false },
  );

  const displayEffect = async (
    breakCount,
    hatchCount,
    updateHatchCount = true,
  ) => {
    const oldMaxStage = getCurentStage(hatchEgg.breakCount);
    const newMaxStage = getCurentStage(breakCount);
    if (hatchEgg.breakCount === null || hatchEgg.hatchCount === null) {
      setStages(
        stages.map((stage) => {
          if (newMaxStage >= stage.number) {
            return { number: stage.number, state: STAGE_STAGE.unlocked };
          } else {
            return { number: stage.number, state: STAGE_STAGE.locked };
          }
        }),
      );
      setBreakCountValue(breakCount);
      setHatchCountValue(hatchCount);
      updatePlayData({
        breakCount: breakCount,
        hatchCount: hatchCount,
      });
      return;
    }
    setStages(
      stages.map((stage) => {
        if (oldMaxStage >= stage.number) {
          return { number: stage.number, state: STAGE_STAGE.unlocked };
        } else {
          return { number: stage.number, state: STAGE_STAGE.locked };
        }
      }),
    );
    setBreakCountValue(hatchEgg.breakCount);
    setHatchCountValue(hatchEgg.hatchCount);
    if (
      hatchEgg.hatchCount >= 0 &&
      hatchEgg.hatchCount !== hatchCount &&
      updateHatchCount
    ) {
      setHatchCountUpdated(true);
    }
    if (hatchEgg.breakCount >= 0 && hatchEgg.breakCount !== breakCount) {
      setBreakCountUpdated(true);
    }
    if (
      (hatchEgg.hatchCount >= 0 &&
        hatchEgg.hatchCount !== hatchCount &&
        updateHatchCount) ||
      (hatchEgg.breakCount >= 0 && hatchEgg.breakCount !== breakCount)
    ) {
      playSound(soundUnlocking);
      await Delay(1000);
      setBreakCountValue(breakCount);
      setHatchCountValue(hatchCount);
      setHatchCountUpdated(false);
      setBreakCountUpdated(false);
    }
    if (hatchEgg.breakCount !== null && newMaxStage !== oldMaxStage) {
      const unlockingStages = stages.map((stage) => {
        if (stage.number > oldMaxStage && stage.number <= newMaxStage) {
          return { number: stage.number, state: STAGE_STAGE.unlocking };
        } else if (stage.number <= oldMaxStage) {
          return { number: stage.number, state: STAGE_STAGE.unlocked };
        }
        return { number: stage.number, state: STAGE_STAGE.locked };
      });
      setStages(unlockingStages);
      await Delay(1000);
      const bossAppealingStages = unlockingStages.map((stage) => {
        if (stage.state === STAGE_STAGE.unlocking) {
          return {
            number: stage.number,
            state: STAGE_STAGE.bossAppealing,
          };
        }
        return stage;
      });
      setStages(bossAppealingStages);
      await Delay(1000);
      setStages(
        bossAppealingStages.map((stage) => {
          if (stage.state === STAGE_STAGE.bossAppealing) {
            return { number: stage.number, state: STAGE_STAGE.unlocked };
          }
          return stage;
        }),
      );
    }
  };

  const handleShortcut2 = () => {
    console.debug(
      `handle key 2 press, current hatching stage: ${hatchingStage}`,
    );
    if (hatchingStage === HATCHING_STAGE.idle) {
      handleEggClick();
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      console.debug('Fetching student info for MainPage');
      try {
        const studentInfo = await getStudentInfoService(student.id);
        const breakCount = studentInfo.data.capture;
        const hatchCount = studentInfo.data.egg;
        displayEffect(breakCount, hatchCount);

        updatePlayData({
          breakCount: studentInfo.data.capture,
          hatchCount: studentInfo.data.egg,
        });
      } catch (error) {
        console.error('Error fetching student info:', error);
      }
    };
    fetchData();
  }, []);

  // useEffect(() => {
  //   const _func = async () => {
  //     // await Delay(1000);
  //     // setStages([
  //     //   { number: 1, state: STAGE_STAGE.unlocked },
  //     //   { number: 2, state: STAGE_STAGE.locked },
  //     //   { number: 3, state: STAGE_STAGE.locked },
  //     //   { number: 4, state: STAGE_STAGE.locked },
  //     //   { number: 5, state: STAGE_STAGE.locked },
  //     // ]);
  //     await Delay(1000);
  //     setStages([
  //       { number: 1, state: STAGE_STAGE.unlocked },
  //       { number: 2, state: STAGE_STAGE.unlocking },
  //       { number: 3, state: STAGE_STAGE.unlocking },
  //       { number: 4, state: STAGE_STAGE.unlocking },
  //       { number: 5, state: STAGE_STAGE.unlocking },
  //     ]);
  //     await Delay(1000);
  //     setStages([
  //       { number: 1, state: STAGE_STAGE.unlocked },
  //       { number: 2, state: STAGE_STAGE.bossAppealing },
  //       { number: 3, state: STAGE_STAGE.bossAppealing },
  //       { number: 4, state: STAGE_STAGE.bossAppealing },
  //       { number: 5, state: STAGE_STAGE.bossAppealing },
  //     ]);
  //     await Delay(1500);
  //     setStages([
  //       { number: 1, state: STAGE_STAGE.unlocked },
  //       { number: 2, state: STAGE_STAGE.unlocked },
  //       { number: 3, state: STAGE_STAGE.unlocked },
  //       { number: 4, state: STAGE_STAGE.unlocked },
  //       { number: 5, state: STAGE_STAGE.unlocked },
  //     ]);
  //     await Delay(1000);
  //   };
  //   _func();
  // }, []);

  const playSound = (soundFile) => {
    if (config.isMutedSound === false) {
      const sound = new UIFx(soundFile);
      sound.play();
    }
  };

  const handleEggClick = async () => {
    console.log('Egg clicked');
    console.debug(
      `hatchCountValue: ${hatchCountValue}, hatchingStage: ${hatchingStage}`,
    );
    if (hatchCountValue <= 0) {
      return;
    }
    // const startTime = Date.now();
    if (hatchingStage !== HATCHING_STAGE.idle) {
      return;
    }

    setHatchingStage(HATCHING_STAGE.waiting);
    try {
      const res = await playHatch(student.id);
      console.log('playHatch result:', res);

      const isSuccess = res.data.result === 'yes';
      // const elapsedTime = Date.now() - startTime;
      // const minimumWaitTime = 2000; // 2 วินาที
      // const remainingWaitTime = Math.max(0, minimumWaitTime - elapsedTime);
      // รอให้ครบ 2 วินาที หรือถ้า server ตอบมาช้ากว่านั้น ก็ใช้เวลา server
      // await new Promise((resolve) => setTimeout(resolve, remainingWaitTime));

      updatePlayData({
        breakCount: res.data.capture,
        hatchCount: res.data.egg,
      });

      setHatchCountValue(res.data.egg);
      if (isSuccess) {
        // setHatchingStage(HATCHING_STAGE.waiting);
        // setBreakCountUpdated(true);
        // setTimeout(() => {
        //   setBreakCountValue(res.data.capture);
        //   setBreakCountUpdated(false);
        // }, 1000);
        displayEffect(res.data.capture, res.data.egg, false);

        await Delay(2000); // รอ 2 วินาที
        setBirdNumber({
          birdNumber: res.data.birdNumber,
          duplicated: res.data.duplicated,
        });
        setHatchingStage(HATCHING_STAGE.success);
      } else {
        setBreakCountValue(res.data.capture);
        setHatchingStage(HATCHING_STAGE.failed);
        setTimeout(() => {
          setHatchingStage(HATCHING_STAGE.idle);
        }, 1000);
      }
    } catch (error) {
      console.error('Error during hatching:', error);
      setHatchingStage(HATCHING_STAGE.failed);
      setTimeout(() => {
        setHatchingStage(HATCHING_STAGE.idle);
      }, 1000);
    }
  };

  const gotoPlayFunc = (stageNumber) => {
    if (hatchEgg.birdEnergy > 0) {
      setStageNumber(stageNumber);
      setGotoPlay(true);
    } else {
      setIsBirdRest(true);
      setTimeout(() => {
        setIsBirdRest(false);
      }, 2000);
    }
  };
  // console.debug('Rendering MainPage with stages:', stages);
  // console.debug('Rendering MainPage with hatchEgg:', hatchEgg);

  const EnergyBar = ({ energyValue }) => {
    const energyImgs = [];
    for (let i = 0; i < energyValue; i++) {
      energyImgs.push(<EnergyImg src={images['main-energy.png']} alt="" />);
    }
    return (
      <EnergyBarBlock>
        <EnergyBarImg src={images['main-energy-bar.png']} alt="" />
        <EnergyBlock>{energyImgs}</EnergyBlock>
      </EnergyBarBlock>
    );
  };

  const Bird = ({ energyValue }) => {
    if (energyValue > 1) {
      return (
        <BirdNormalButton
          src={images['main-bird-normal.png']}
          onClick={() => {
            console.log('Bird clicked');
            setBirdNumber({ birdNumber: null, duplicated: false });
            gotoCollectionPage();
          }}
        />
      );
    } else if (energyValue === 1) {
      return (
        <BirdEnergyLeft1Button
          src={images['main-bird-1.png']}
          onClick={() => {
            console.log('Bird clicked');
            setBirdNumber({ birdNumber: null, duplicated: false });
            gotoCollectionPage();
          }}
        />
      );
    } else {
      return (
        <BirdEnergyEmptyButton
          src={images['main-bird-0.png']}
          onClick={() => {
            console.log('Bird clicked');
            setBirdNumber({ birdNumber: null, duplicated: false });
            gotoCollectionPage();
          }}
        />
      );
    }
  };

  return (
    <Container>
      <Background src={images['main-background.jpg']} />
      <HomeButton
        src={images['main-home.png']}
        onClick={() => {
          updatePlayData({ breakCount: null, hatchCount: null });
          history.push('/hall');
        }}
      />
      <ReactSound
        url={soundBackground}
        playStatus={config.isMutedSound ? 'STOPPED' : 'PLAYING'}
        loop={true}
      ></ReactSound>
      <EnergyBar energyValue={hatchEgg.birdEnergy} />
      <Bird energyValue={hatchEgg.birdEnergy} />
      {isBirdRest && (
        <>
          <BirdRestDialog src={images['main-bird-rest-dialog.png']} />
          <ReactSound
            url={soundBirdRest}
            playStatus={config.isMutedSound ? 'STOPPED' : 'PLAYING'}
            loop={false}
          ></ReactSound>
        </>
      )}

      <MainNumber
        color="red"
        number={breakCountValue}
        top={288}
        left={200}
        height={60}
      />
      {breakCountUpdated && (
        <AnimationWithSound
          imageUrl={images['main-unlocking.png']}
          imageTop={160}
          imageLeft={140}
          imageWidth={287}
          imageHeight={290}
          playSound={!config.isMutedSound}
        />
      )}
      <MainNumber
        color="green"
        number={hatchCountValue}
        top={542}
        left={445}
        height={60}
      />
      {hatchCountUpdated && (
        <AnimationWithSound
          imageUrl={images['main-unlocking.png']}
          imageTop={410}
          imageLeft={382}
          imageWidth={287}
          imageHeight={290}
          playSound={!config.isMutedSound}
        />
      )}
      <MainStage
        number={1}
        stageState={stages[0].state}
        unlocked={{ top: 720, left: 980, width: 349, height: 280 }}
        locked={{ top: 720, left: 980, width: 349, height: 280 }}
        unlocking={{ top: 720, left: 980, width: 287, height: 290 }}
        handleOnClick={() => gotoPlayFunc(1)}
      />

      <MainStage
        number={2}
        stageState={stages[1].state}
        unlocked={{ top: 800, left: 1510, width: 294, height: 246 }}
        locked={{ top: 800, left: 1490, width: 193, height: 155 }}
        unlocking={{ top: 710, left: 1440, width: 287, height: 290 }}
        handleOnClick={() => gotoPlayFunc(2)}
      />

      <MainStage
        number={3}
        stageState={stages[2].state}
        unlocked={{ top: 510, left: 1510, width: 362, height: 203 }}
        locked={{ top: 522, left: 1704, width: 193, height: 155 }}
        unlocking={{ top: 432, left: 1654, width: 287, height: 290 }}
        handleOnClick={() => gotoPlayFunc(3)}
      />

      <MainStage
        number={4}
        stageState={stages[3].state}
        unlocked={{ top: 260, left: 490, width: 422, height: 207 }}
        locked={{ top: 250, left: 460, width: 189, height: 170 }}
        unlocking={{ top: 160, left: 410, width: 287, height: 290 }}
        handleOnClick={() => gotoPlayFunc(4)}
      />

      <MainStage
        number={5}
        stageState={stages[4].state}
        unlocked={{ top: 30, left: 1180, width: 529, height: 331 }}
        locked={{ top: 15, left: 1170, width: 182, height: 181 }}
        unlocking={{ top: -75, left: 1120, width: 287, height: 290 }}
        handleOnClick={() => gotoPlayFunc(5)}
      />
      {gotoPlay && (
        <AnimationWithSound
          imageUrl={images['main-goto-play.png']}
          soundFile={soundGotoPlay}
          playSound={!config.isMutedSound}
          onSoundEnd={() => {
            setGotoPlay(false);
            playStage({
              stageNumber: stageNumber,
              playId: student.id + '-' + Date.now(),
            });
          }}
        />
      )}

      <EggButton src={images['main-egg.png']} onClick={handleEggClick} />

      {hatchingStage === HATCHING_STAGE.waiting && (
        <AnimationWithSound
          imageBottom={-40}
          imageLeft={-55}
          imageWidth={350}
          imageHeight={350}
          imageUrl={images['main-hatching-waiting.png']}
          soundFile={null}
        />
      )}
      {hatchingStage === HATCHING_STAGE.failed && (
        <AnimationWithSound
          imageBottom={-40}
          imageLeft={-55}
          imageWidth={350}
          imageHeight={350}
          imageUrl={images['main-hatching-failed.png']}
          soundFile={soundHatchingFailed}
          playSound={!config.isMutedSound}
        />
      )}
      {hatchingStage === HATCHING_STAGE.success && (
        <AnimationWithSound
          imageBottom={-40}
          imageLeft={-55}
          imageWidth={350}
          imageHeight={350}
          imageUrl={images['main-hatching-success.png']}
          soundFile={soundHatchingSuccess}
          playSound={!config.isMutedSound}
          onClick={() => {
            console.log('Hatching success screen clicked');
            // setHatchingStage(HATCHING_STAGE.idle);
            gotoCollectionPage();
          }}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  width: 1920px;
  height: 1080px;
  overflow: hidden;
`;

const Background = styled.img`
  top: 0px;
  left: 0px;
  width: 1920px;
  height: 1080px;
  position: absolute;
  object-fit: contain;
`;

const HomeButton = styled(ImgClick)`
  position: absolute;
  top: 20px;
  left: 20px;
  width: 152px;
  height: 103px;
`;

const EggButton = styled(ImgClick)`
  position: absolute;
  top: 857px;
  left: 42px;
  width: 159px;
  height: 177px;
`;
const BirdNormalButton = styled(ImgClick)`
  position: absolute;
  top: 388px;
  left: 0px;
`;

const BirdEnergyLeft1Button = styled(ImgClick)`
  position: absolute;
  top: 410px;
  left: 0px;
`;

const BirdEnergyEmptyButton = styled(ImgClick)`
  position: absolute;
  top: 480px;
  left: 0px;
`;

const BirdRestDialog = styled.img`
  position: absolute;
  top: 320px;
  left: 50px;
`;

const Stage1 = styled(ImgClick)`
  position: absolute;
  left: 980px;
  top: 720px;
  width: 349px;
  height: 280px;
`;
const Stage2Locked = styled(ImgClick)`
  position: absolute;
  left: 1490px;
  top: 800px;
  width: 193px;
  height: 155px;
`;
const Stage2 = styled(ImgClick)`
  position: absolute;
  left: 1510px;
  top: 800px;
  width: 294px;
  height: 246px;
`;
const Stage3Locked = styled(ImgClick)`
  position: absolute;
  left: 1710px;
  top: 530px;
  width: 188px;
  height: 146px;
`;
const Stage3 = styled(ImgClick)`
  position: absolute;
  left: 1510px;
  top: 510px;
  width: 362px;
  height: 203px;
`;
const Stage4Locked = styled(ImgClick)`
  position: absolute;
  left: 460px;
  top: 250px;
  width: 189px;
  height: 170px;
`;
const Stage4 = styled(ImgClick)`
  position: absolute;
  left: 490px;
  top: 260px;
  width: 422px;
  height: 207px;
`;
const Stage5Locked = styled(ImgClick)`
  position: absolute;
  left: 1170px;
  top: 15px;
  width: 182px;
  height: 181px;
`;
const Stage5 = styled(ImgClick)`
  position: absolute;
  left: 1180px;
  top: 30px;
  width: 529px;
  height: 331px;
`;

const GotoEffect = styled(Img)`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 1920px;
  height: 1080px;
`;

const FullScreenEffect = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 1920px;
  height: 1080px;
  background-color: rgba(0, 0, 0, 0.9);
`;

const EggImgEffect = styled(Img)`
  position: absolute;
  bottom: -40px;
  left: -55px;
  overflow: hidden;
`;

const NormalImage = styled.img`
  position: absolute;
  top: ${(props) => props.$top}px;
  left: ${(props) => props.$left}px;
`;

const EnergyBarBlock = styled.div`
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 361px;
`;

const EnergyBarImg = styled.img`
  position: absolute;
`;

const EnergyBlock = styled.div`
  position: absolute;
  display: flex;
  top: 55px;
  left: 135px;
  gap: 8px;
`;

const EnergyImg = styled.img`
  // width: 80px;
`;

const mapStateToProps = ({ student, config, hatchEgg }) => ({
  student,
  config,
  hatchEgg,
});

const mapDispatchToProps = {
  // Your dispatch actions here
  playStage,
  gotoCollectionPage,
  setBirdNumber,
  updatePlayData,
};

export default connect(mapStateToProps, mapDispatchToProps)(MainPage);
